import { VOICED_STOP_HEADER_PRIORITY } from '@shared/constants';
import useFeatures from '@shared/hooks/useFeatures';
import { StopHeader } from '@shared/models/stopHeader';
import speak from '@shared/utils/speak';
import { useCallback, useEffect, useRef } from 'react';
import useManuallyTriggeredAudio from './useManuallyTriggeredAudio';
import { DeepReadonly } from 'ts-essentials';
import { DepartureRow } from '@modules/departures/models/departureRow';
import { getPlatformName } from '@shared/utils/platformName';

function createHeaderText(
  stopAreaName: string,
  isShowingPlatforms: boolean,
  platforms: readonly string[] | undefined,
  trainPlatforms: ReadonlySet<string>,
): string {
  if (!isShowingPlatforms || !platforms || platforms.length === 0) {
    return stopAreaName;
  }

  const tracks = platforms.filter((platform) => trainPlatforms.has(platform));
  const otherPlatforms = platforms.filter(
    (platform) => !trainPlatforms.has(platform),
  );

  const parts = new Array<string>();
  if (otherPlatforms.length > 0) {
    parts.push(`${getPlatformName('bus')} ${otherPlatforms.join(', ')}`);
  }
  if (tracks.length > 0) {
    parts.push(`${getPlatformName('train')} ${tracks.join(', ')}`);
  }

  return `${stopAreaName} ${parts.join(', ')}`;
}

export default function useVoicedStopHeader(
  stopHeader: DeepReadonly<StopHeader> | undefined,
  isShowingPlatforms: boolean,
  departureRows: DeepReadonly<DepartureRow[]> | undefined,
) {
  const trainPlatforms = useRef(new Set<string>());
  const { manualAudio } = useFeatures();

  useEffect(() => {
    if (!manualAudio || !departureRows) {
      return;
    }

    for (const row of departureRows) {
      if (row.transportMode === 'train' && row.platform) {
        trainPlatforms.current.add(row.platform);
      }
    }
  }, [departureRows, manualAudio]);

  // Handle case where only one platform is configured, where we then don't receive platform information from departure rows
  useEffect(() => {
    if (
      !manualAudio ||
      !departureRows ||
      departureRows.length === 0 ||
      stopHeader?.platforms?.length !== 1
    ) {
      return;
    }

    const platform = stopHeader.platforms[0];

    if (
      platform &&
      departureRows.some((row) => row.transportMode === 'train')
    ) {
      trainPlatforms.current.add(platform);
    }
  }, [departureRows, stopHeader, manualAudio]);

  const voiceHeader = useCallback(async () => {
    if (!stopHeader) {
      return;
    }

    await speak(
      createHeaderText(
        stopHeader.stopAreaName,
        isShowingPlatforms,
        stopHeader.platforms,
        trainPlatforms.current,
      ),
    );
  }, [stopHeader, isShowingPlatforms]);

  useManuallyTriggeredAudio(VOICED_STOP_HEADER_PRIORITY, voiceHeader);
}
